/**
 * "Import from connector" — the Project Controls entry point into the same
 * P6 / Microsoft Project import that Settings → Connectors runs.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plug, ExternalLink } from 'lucide-react';
import { ScheduleImportPanel } from '@/components/connectors/ScheduleImportPanel';

export const ConnectorScheduleImportDialog = ({
  open, onOpenChange, portfolioProjectId, onImported,
}) => {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/connectors/schedule');
        setList(data.connectors || []);
        setActive((data.connectors || [])[0]?.name || null);
      } catch (e) {
        setError(e?.response?.status === 403
          ? 'Importing from a connector is administrative — your role cannot do it.'
          : 'Could not load the schedule connectors.');
        setList([]);
      }
    })();
  }, [open]);

  const current = list?.find(c => c.name === active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="connector-schedule-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plug className="w-4 h-4 text-emerald-500" /> Import from a connector
          </DialogTitle>
          <DialogDescription>
            Pulls the activity list straight out of Primavera P6 or Microsoft Project.
            Credentials live in Settings → Connectors.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-xs text-red-500" data-testid="connector-import-error">{error}</p>}
        {!list && !error && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading connectors…
          </div>
        )}

        {list?.length > 0 && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {list.map(c => (
                <button key={c.name} onClick={() => setActive(c.name)}
                        className={`px-2.5 py-1 rounded-full border text-[11px] ${
                          active === c.name
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600'
                            : 'border-border text-muted-foreground'}`}
                        data-testid={`connector-import-pick-${c.name}`}>
                  {c.label}
                  {!c.configured && (
                    <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0">
                      not set up
                    </Badge>
                  )}
                </button>
              ))}
            </div>

            {current && !current.configured ? (
              <div className="text-xs text-muted-foreground space-y-1"
                   data-testid="connector-import-unconfigured">
                <p>
                  <strong>{current.label}</strong> has no connection details yet, so there is
                  nothing to import from. Still needed:
                </p>
                <ul className="list-disc pl-5">
                  {(current.missing || []).map(m => (
                    <li key={m.field}>{m.field} — {m.needs}</li>
                  ))}
                </ul>
                <a href={`/settings/connectors?c=${current.name}`}
                   className="inline-flex items-center gap-1 text-sky-500"
                   data-testid="connector-import-settings-link">
                  <ExternalLink className="w-3 h-3" /> Open Settings → Connectors
                </a>
              </div>
            ) : current ? (
              <ScheduleImportPanel connector={current.name} label={current.label}
                                   portfolioProjectId={portfolioProjectId}
                                   onImported={(d) => { onImported?.(d); onOpenChange(false); }} />
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConnectorScheduleImportDialog;
